import React, { useState, useEffect, useRef } from 'react';
import type { Result } from '../types';

interface Props {
  currentResult: Result;
  nearbyResults: Result[];
  onResultClick?: (result: Result) => void;
}

export default function FinishOverview({ currentResult, nearbyResults }: Props) {
  const [expandedResult, setExpandedResult] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setExpandedResult(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get initials from a name
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  // Convert HH:MM:SS to seconds
  const timeToSeconds = (time: string): number => {
    if (!time || time === 'DNF') return 0;
    const [hours, minutes, seconds] = time.split(':').map(Number);
    return hours * 3600 + minutes * 60 + seconds;
  };

  // Format time difference
  const formatTimeDiff = (diff: number): string => {
    if (diff === 0) return 'Same time';
    if (diff < 0) return `${Math.abs(diff).toFixed(1)}s ahead`;
    return `${diff.toFixed(1)}s behind`;
  };

  const currentTime = timeToSeconds(currentResult.chip_time || '');

  // Filter results to only show those within 59 seconds
  const filteredResults = nearbyResults
    .filter(r => {
      if (r.id === currentResult.id) return false;
      const resultTime = timeToSeconds(r.chip_time || '');
      const timeDiff = Math.abs(resultTime - currentTime);
      return timeDiff <= 59;
    })
    .sort((a, b) => (a.rank || Infinity) - (b.rank || Infinity));

  // Calculate vertical offsets with more spacing
  const verticalOffsets = filteredResults.reduce((acc, result, index) => {
    // Alternate between top and bottom with increasing magnitude
    const baseOffset = Math.floor((index + 1) / 2) * 25; // Increase spacing between rows
    acc[result.id] = index % 2 === 0 ? -baseOffset : baseOffset;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div ref={containerRef} className="relative w-full h-48">
      {/* Finish line */}
      <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-gradient-to-b from-transparent via-gray-900 to-transparent" />
      <div className="absolute left-1/2 h-full -ml-4 -mr-4 w-8 bg-repeat-y opacity-10" 
        style={{ 
          backgroundImage: 'linear-gradient(45deg, black 25%, transparent 25%, transparent 75%, black 75%, black), linear-gradient(45deg, black 25%, transparent 25%, transparent 75%, black 75%, black)',
          backgroundSize: '8px 8px',
          backgroundPosition: '0 0, 4px 4px'
        }} 
      />

      {/* Current participant (centered) */}
      <div 
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20"
      >
        <div
          className="w-12 h-12 bg-gray-900 text-white rounded-full shadow-lg flex items-center justify-center font-medium text-lg"
        >
          {getInitials(currentResult.name)}
        </div>
      </div>

      {/* Other results dots */}
      <div className="relative h-full">
        {filteredResults.map((result) => {
          const resultTime = timeToSeconds(result.chip_time || '');
          const timeDiff = resultTime - currentTime;
          const isExpanded = expandedResult === result.id;
          
          // Calculate horizontal position based on time difference
          const maxSpread = 40; // Maximum spread percentage from center
          const spreadPerSecond = 10; // Percentage spread per second difference
          const xPos = Math.min(maxSpread, (Math.abs(timeDiff) / 5) * spreadPerSecond);
          const position = timeDiff >= 0 ? 50 - xPos : 50 + xPos;

          // Calculate vertical position with alternating pattern
          const yOffset = verticalOffsets[result.id];
          
          // Adjust expanded bubble position to avoid edges
          let expandedXPos = position;
          if (isExpanded) {
            if (position < 25) expandedXPos = 25;
            if (position > 75) expandedXPos = 75;
          }

          return (
            <div
              key={result.id}
              className={`absolute transform transition-all duration-300 ${
                isExpanded ? 'z-30' : 'z-0'
              }`}
              style={{ 
                left: `${isExpanded ? expandedXPos : position}%`,
                top: '50%',
                transform: `translate(-50%, calc(-50% + ${yOffset}px))`,
              }}
            >
              <button
                onClick={() => {
                  if (isExpanded) {
                    setExpandedResult(null);
                  } else {
                    setExpandedResult(result.id);
                  }
                }}
                className={`relative transition-all duration-300 ${
                  timeDiff >= 0
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
                } rounded-full shadow-md ${
                  isExpanded ? 'w-auto h-auto p-3' : 'w-10 h-10'
                } flex items-center justify-center font-medium`}
              >
                {isExpanded ? (
                  <div className="flex flex-col items-center whitespace-nowrap">
                    <span className="font-bold">{result.name}</span>
                    <span className="text-sm opacity-90">{result.chip_time}</span>
                    <span className="text-xs opacity-75">
                      {formatTimeDiff(timeDiff)}
                    </span>
                  </div>
                ) : (
                  getInitials(result.name)
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}